/**
 * Enrolamiento TOTP -- primera vez que un usuario con `auth.require_mfa`
 * activo (apagado por defecto, ver README del backend) inicia sesión sin
 * tener el segundo factor configurado todavía. Llega aquí via
 * RequireSession -> guards.tsx, que redirige cuando `mfa === 'not_enrolled'`.
 *
 * Sin generación de QR (evita sumar una librería solo para esto): se
 * muestra el secreto en texto para ingreso manual -- cualquier app TOTP
 * (Google Authenticator, Authy, 1Password, etc.) lo acepta igual que un QR.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { mfaEnrollConfirm, mfaEnrollStart, type MfaEnrollStart } from '../lib/backend';
import { Wordmark } from '../components/Logo';

export function MfaEnroll() {
  const { pendingMfaToken, completeMfa } = useAuth();
  const nav = useNavigate();
  const [start, setStart] = useState<MfaEnrollStart | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [confirmErr, setConfirmErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pendingMfaToken) return;
    mfaEnrollStart(pendingMfaToken)
      .then(setStart)
      .catch(() => setLoadErr('No se pudo generar el segundo factor. Intente iniciar sesión nuevamente.'));
  }, [pendingMfaToken]);

  if (!pendingMfaToken) {
    return (
      <div className="authwrap">
        <div className="authcard">
          <p className="muted">Su sesión de verificación expiró. Vuelva a iniciar sesión.</p>
          <button className="btn btn--primary" style={{ marginTop: 12 }} onClick={() => nav('/login', { replace: true })}>
            Volver al inicio de sesión
          </button>
        </div>
      </div>
    );
  }

  async function confirm() {
    if (!pendingMfaToken) return;
    setBusy(true);
    setConfirmErr(null);
    try {
      const token = await mfaEnrollConfirm(pendingMfaToken, code.trim());
      await completeMfa(token);
      nav('/', { replace: true });
    } catch {
      setConfirmErr('Código incorrecto. Verifique la hora de su dispositivo e intente de nuevo.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="authwrap">
      <div className="authcard" style={{ maxWidth: 420 }}>
        <div className="authbrand">
          <Wordmark size={38} />
        </div>
        <h2 style={{ fontSize: 19, marginBottom: 4 }}>Configurar verificación en dos pasos</h2>
        <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 18 }}>
          Esta institución exige un segundo factor de autenticación. Configúrelo una sola vez con
          una aplicación como Google Authenticator, Authy o 1Password.
        </p>

        {loadErr && <div className="callout callout--crit" style={{ marginBottom: 14 }}>{loadErr}</div>}

        {start && (
          <>
            <div className="field">
              <label>1. Agregue esta clave en su aplicación de autenticación</label>
              <div
                className="mono"
                style={{
                  padding: '10px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r-sm)',
                  background: 'var(--ground)',
                  fontSize: 14,
                  letterSpacing: 1,
                  wordBreak: 'break-all',
                }}
              >
                {start.secret}
              </div>
              <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                Cuenta nueva → "Ingresar clave de configuración manualmente" → pegue esta clave.
              </p>
            </div>

            <div className="field">
              <label htmlFor="mfa-code">2. Ingrese el código de 6 dígitos que muestra la aplicación</label>
              <input
                id="mfa-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                style={{ fontSize: 18, letterSpacing: 4, textAlign: 'center' }}
              />
              {confirmErr && <div className="errmsg">{confirmErr}</div>}
            </div>

            <button
              className="btn btn--primary"
              style={{ width: '100%', justifyContent: 'center' }}
              disabled={busy || code.length !== 6}
              onClick={() => void confirm()}
            >
              {busy ? 'Verificando…' : 'Confirmar y continuar'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
